import { useEffect, useState } from 'react';
import { useDatabase } from '../contexts/DatabaseContext';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Card, Button } from './primitives';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { LuCopy } from 'react-icons/lu';
import { useTranslation } from '../hooks/useTranslation';

export default function TeamsRegistrationComponent({isPopup = false, workspaceId = null, closePopup}) {
    const { clientData } = useOutletContext();
    const [token, setToken] = useState(clientData?.tempToken);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { createTeamsChannelConfig, generateTeamsChannelConfig } = useDatabase();

    async function generateToken() {
        try {
            const docRef = generateTeamsChannelConfig();
            clientData.tempToken = docRef.id;
            setToken(docRef.id);
            await createTeamsChannelConfig(docRef, workspaceId);
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!token) {
            generateToken();
        } else {
            setLoading(false);
        }
    }, []);

    return (
        <div className="analytics-container teams-registrations">
            <RegistrationCard 
                error={error}
                loading={loading}
                setLoading={setLoading}
                token={token}
                isPopup={isPopup}
                closePopup={closePopup}
            />
        </div>
    );
}

function RegistrationCard({ error, loading, setLoading, token, isPopup, closePopup }) {
    const t = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <Card className={isPopup ? 'teams-addchannel-popup' : ''}>
                <Card.Body>
                    {!isPopup && ( 
                        <>
                            <h2>Register Teams</h2>
                            <p>Copy the token and paste it into the Teams channel to complete the registration.</p>
                        </>
                    )}
                    {isPopup && ( 
                        <>
                            <h2>Add New Channel</h2>
                            <p>If you are adding a channel from a new team, you can add it with by installing the bot through <b>this link</b>.</p>
                            <p>When the bot requests a pairing code, paste the code from the bottom of this screen.</p>
                            <p>If you are adding a channel from an existing team, you can add it by following these steps:</p>
                            <ol style={{ marginLeft: '10px', paddingLeft: '10px' }}>
                                <li>In the channel you would like to add, create a new message and type '@'.</li>
                                <li>Locate the Page Parrot bot in the list and select it.</li>
                                <li>Just send any message with the word "pair" included, and the bot will present you with a dialog.</li>
                            </ol>
                            <p>Copy the token and paste it into the Teams channel to add a new channel.</p>
                        </>
                    )}
                    <p><b>{t('Token')}:</b> <span className="token-text">{token}</span></p>
                    <CopyToClipboard text={token} onCopy={handleCopy}>
                        <Button disabled={loading} loading={loading}>
                            <LuCopy className="copy-icon"/>{copied ? t('Copied!') : t('Copy Token')}
                        </Button>
                    </CopyToClipboard>
                    {isPopup && (
                        <Button className="error" onClick={closePopup}>
                            {t('Close')}
                        </Button>
                    )}
                </Card.Body>
            </Card>
            {token && <TokenListener error={error} token={token} setLoading={setLoading} isPopup={isPopup} closePopup={closePopup}/>}
        </>
    );
}

function TokenListener({ error, token, setLoading, isPopup, closePopup }) {
    const t = useTranslation();
    const { useTeamsChannelConfigDoc, deleteTeamsChannelConfig } = useDatabase();
    const { clientData } = useOutletContext();
    const [data, loading, err] = useTeamsChannelConfigDoc(token);
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [loading]);

    useEffect(() => {
        async function deleteToken() {
            await deleteTeamsChannelConfig(token);
            clientData.tempToken = null;
            if (!isPopup) {
                navigate('/auth/dashboard');
            } else {
                closePopup?.();
            }
        }

        if (error || err) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: "Error generating token."
                }
            }));
        }
        if (data && data.initialized) {
            deleteToken();
        }
    }, [data]);

    return null;
} 